// ── Types d'inscription supportés (URL slug) ───────────────────
export type UserType = 'client' | 'livreur' | 'livreur-pro';

/** Nom du type côté API Laravel (types.name) */
export type BackendTypeName =
    | 'client'
    | 'community_delivery'
    | 'professional_delivery';

export type StepId =
    | 'contact'
    | 'identity'
    | 'vehicle'
    | 'availability'
    | 'documents'
    | 'consents';

export interface StepConfig {
    id: StepId;
    label: string;
}

export interface UserTypeConfig {
    backendTypeName: BackendTypeName;
    jwtRoles: string[];
    label: string;
    icon: string;
    color: string;
    description: string;
    dashboardPath: string;
    steps: StepConfig[];
    emailRequired: boolean;
}

export const USER_TYPE_CONFIG: Record<UserType, UserTypeConfig> = {
    client: {
        backendTypeName: 'client',
        jwtRoles: ['client'],
        label: 'Client',
        icon: 'ri-user-line',
        color: 'teal',
        description: 'Profitez de livraisons économiques partout',
        dashboardPath: '/dashboard/client',
        emailRequired: true,
        steps: [
            { id: 'contact', label: 'Informations' },
            { id: 'identity', label: 'Zone' },
            { id: 'consents', label: 'Finalisation' },
        ],
    },
    livreur: {
        backendTypeName: 'community_delivery',
        jwtRoles: ['community_delivery'],
        label: 'Livreur Communautaire',
        icon: 'ri-community-line',
        color: 'orange',
        description: 'Livraisons flexibles dans votre quartier',
        dashboardPath: '/dashboard/livreur',
        emailRequired: true,
        steps: [
            { id: 'contact', label: 'Inscription' },
            { id: 'identity', label: 'Zone' },
            { id: 'vehicle', label: 'Véhicule' },
            { id: 'availability', label: 'Disponibilité' },
            { id: 'documents', label: 'Documents' },
            { id: 'consents', label: 'Validation' },
        ],
    },
    'livreur-pro': {
        backendTypeName: 'professional_delivery',
        jwtRoles: ['professional_delivery'],
        label: 'Livreur Professionnel',
        icon: 'ri-briefcase-line',
        color: 'blue',
        description: 'Carrière professionnelle avec validation de dossier',
        dashboardPath: '/dashboard/livreur-pro',
        emailRequired: true,
        steps: [
            { id: 'contact', label: 'Inscription' },
            { id: 'identity', label: 'Zone' },
            { id: 'vehicle', label: 'Véhicule' },
            { id: 'availability', label: 'Disponibilité' },
            { id: 'documents', label: 'Documents' },
            { id: 'consents', label: 'Validation' },
        ],
    },
};

export const REGISTERABLE_USER_TYPES: UserType[] = ['client', 'livreur', 'livreur-pro'];

export const INTERNAL_ROLES = [
    { id: 'client', label: 'Membre', icon: 'ri-user-line' },
    { id: 'community_delivery', label: 'Livreur', icon: 'ri-truck-line' },
    { id: 'admin', label: 'Administrateur', icon: 'ri-shield-line' },
];

export const ROLE_DASHBOARD_MAP: Record<string, string> = {
    client: '/dashboard/client',
    community_delivery: '/dashboard/livreur',
    professional_delivery: '/dashboard/livreur-pro',
    company: '/dashboard/company',
    admin: '/dashboard/admin',
    super_admin: '/dashboard/super-admin',
};

export interface RegisterReference {
    types: { id: number; name: string }[];
    zones: { id: number; name: string }[];
    vehicle_types: { id: number; name: string }[];
    specializations: { id: number; name: string }[];
    availability_slots: {
        id: number;
        name: string;
        is_week_based: boolean;
        start_time: string | null;
        end_time: string | null;
    }[];
    categories: { id: number; name: string; type?: string }[];
}

export interface FormState {
    firstName: string;
    lastName: string;
    phone: string;
    phoneCountryCode: string;
    email: string;
    password: string;
    confirmPassword: string;
    zoneId: string;
    vehicleTypeId: string;
    capacityWeightKg: string;
    capacityVolumeM3: string;
    specializationIds: number[];
    availabilitySlotIds: number[];
    coverageZoneIds: number[];
    documents: Record<string, File | null>;
    acceptTerms: boolean;
    acceptPrivacy: boolean;
    acceptQuality: boolean;
    acceptVerification: boolean;
    acceptNotifications: boolean;
}

export const EMPTY_USER: FormState = {
    firstName: '',
    lastName: '',
    phone: '',
    phoneCountryCode: '+224',
    email: '',
    password: '',
    confirmPassword: '',
    zoneId: '',
    vehicleTypeId: '',
    capacityWeightKg: '',
    capacityVolumeM3: '',
    specializationIds: [],
    availabilitySlotIds: [],
    coverageZoneIds: [],
    documents: {},
    acceptTerms: false,
    acceptPrivacy: false,
    acceptQuality: false,
    acceptVerification: false,
    acceptNotifications: false,
};

export function getConfig(userType: string): UserTypeConfig | null {
    return USER_TYPE_CONFIG[userType as UserType] ?? null;
}

export function isValidUserType(userType: string): userType is UserType {
    return userType in USER_TYPE_CONFIG;
}

export function isDriverUserType(userType: UserType): boolean {
    return userType === 'livreur' || userType === 'livreur-pro';
}

export function resolveDashboard(roleCode: string): string {
    return ROLE_DASHBOARD_MAP[roleCode] ?? '/';
}

export function buildRegisterPayload(
    form: FormState,
    userType: UserType,
    reference: RegisterReference,
): Record<string, unknown> {
    const config = USER_TYPE_CONFIG[userType];
    const phone = `${form.phoneCountryCode}${form.phone.replace(/\D/g, '')}`;

    const type = reference.types.find(t => t.name === config.backendTypeName);
    if (!type) {
        throw new Error(`Type d'inscription introuvable : ${config.backendTypeName}`);
    }

    if (!form.zoneId) {
        throw new Error('La zone est obligatoire.');
    }

    const payload: Record<string, unknown> = {
        firstnames: form.firstName.trim(),
        lastname: form.lastName.trim(),
        phone,
        password: form.password,
        password_confirmation: form.confirmPassword,
        type_id: type.id,
        zone_id: Number(form.zoneId),
    };

    if (form.email.trim()) {
        payload.email = form.email.trim();
    }

    if (isDriverUserType(userType)) {
        payload.vehicle_capacity = {
            vehicle_type_id: Number(form.vehicleTypeId),
            max_weight: Number(form.capacityWeightKg),
            max_volume: Number(form.capacityVolumeM3),
        };
        payload.specialization_ids = form.specializationIds;
        payload.availability_slot_ids = form.availabilitySlotIds;
        payload.zone_ids = form.coverageZoneIds.length > 0
            ? form.coverageZoneIds
            : [Number(form.zoneId)];

        const documents = buildDocumentsPayload(form);
        if (documents.length > 0) {
            payload.documents = documents;
        }
    }

    return payload;
}

function buildDocumentsPayload(form: FormState): Array<Record<string, unknown>> {
    const startedAt = new Date().toISOString().slice(0, 10);
    const endedAt = new Date(Date.now() + 730 * 86400000).toISOString().slice(0, 10);
    const result: Array<Record<string, unknown>> = [];

    Object.entries(form.documents).forEach(([key, file]) => {
        if (!file) return;

        const categoryId = Number(key);
        if (!categoryId) return;

        result.push({
            category_id: categoryId,
            file_url: file.name,
            started_at: startedAt,
            ended_at: endedAt,
        });
    });

    return result;
}
